import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import PageWrapper from "../../../../components/layout/page/PageWrapper";
import PageHeader from "../../../../components/layout/page/PageHeader";
import ContentPanel from "../../../../components/layout/content/ContentPanel";
import Heading from "../../../../components/typography/Heading";
import Text from "../../../../components/typography/Text";
import EmptyState from "../../../../components/feedback/EmptyState";

import SecondaryPillTabs from "../../../../components/tabs/SecondaryPillTabs";
import Button from "../../../../components/buttons/Button";
import BackButton from "../../../../components/buttons/back/BackButton";

import TalentAvatar from "../../components/avatar/TalentAvatar";

import TalentOverviewView from "../../private/views/TalentOverviewView";
import TalentDetailsView from "../../private/views/TalentDetailsView";
import TalentSportsView from "../../private/views/TalentSportsView";
import TalentExperiencesView from "../../private/views/TalentExperiencesView";
import TalentAchievementsView from "../../private/views/TalentAchievementsView";
import TalentMediaView from "../../private/views/TalentMediaView";
import PrivateTalentPulseView from "../../private/pulse/views/PrivateTalentPulseView";

import { useSharedTalent } from "../hooks/useSharedTalent";

import "../../private/pages/PrivateTalentPage.css";
import "./SharedTalentProfilePage.css";

const getCount = (items) => (Array.isArray(items) ? items.length : 0);

export default function SharedTalentProfilePage() {
  const navigate = useNavigate();
  const { talentTag } = useParams();

  const [activeTab, setActiveTab] = useState("overview");

  const {
    profile,
    sports,
    experiences,
    achievements,
    media,
    loading,
    error,
    refresh,
  } = useSharedTalent(talentTag);

  const talentProfile = profile
    ? {
        ...profile,
        sports: Array.isArray(sports) ? sports : [],
        experiences: Array.isArray(experiences) ? experiences : [],
        achievements: Array.isArray(achievements) ? achievements : [],
      }
    : null;

  const tabs = [
    {
      id: "overview",
      label: "Overview",
    },
    {
      id: "sports",
      label: `Sports (${getCount(sports)})`,
    },
    {
      id: "experiences",
      label: `Experience (${getCount(experiences)})`,
    },
    {
      id: "achievements",
      label: `Achievements (${getCount(achievements)})`,
    },
    {
      id: "media",
      label: `Media (${getCount(media)})`,
    },
  ];

  const renderView = () => {
    switch (activeTab) {
      case "sports":
        return <TalentSportsView sports={sports} readOnly />;

      case "experiences":
        return <TalentExperiencesView experiences={experiences} readOnly />;

      case "achievements":
        return <TalentAchievementsView achievements={achievements} readOnly />;

      case "media":
        return <TalentMediaView media={media} readOnly />;

      default:
        return (
          <div className="user-talent__overview-layout">
            <div className="user-talent__overview-main">
              <TalentOverviewView profile={talentProfile} readOnly />

              {profile?.talentId && (
                <PrivateTalentPulseView
                  talentId={profile.talentId}
                  talentTag={profile.talentTag}
                  sports={sports}
                  readOnly
                />
              )}
            </div>

            <aside className="user-talent__overview-aside">
              <TalentDetailsView profile={talentProfile} />
            </aside>
          </div>
        );
    }
  };

  return (
    <PageWrapper>
      <BackButton onClick={() => navigate("/talents")} label="Talents" />


      {loading && !profile ? (
        <Text as="p" tone="secondary" size="sm">
          Loading Talent ID...
        </Text>
      ) : error || !profile ? (
        <div className="shared-talent-profile__empty">
          <EmptyState
            title="Talent ID unavailable"
            description={error || "This Talent ID is not available."}
          />

          <Button value="Retry" variant="primary-outlined" onClick={refresh} />
        </div>
      ) : (
        <>
          <ContentPanel className="user-talent__hero">
            <TalentAvatar profile={profile} size="large" />

            <div className="user-talent__identity">
              <Text as="span" className="user-talent__eyebrow">
                TALENT
              </Text>

              <Heading as="h2" size="lg">
                {profile.name || `Talent ID #${profile.talentId}`}
              </Heading>

              <Text as="p" tone="secondary" size="sm">
                {[profile.talentTag, profile.visibility || "Public"]
                  .filter(Boolean)
                  .join(" · ")}
              </Text>
            </div>
          </ContentPanel>

          <div className="user-talent__tabs">
            <SecondaryPillTabs
              tabs={tabs}
              activeTab={activeTab}
              onChange={setActiveTab}
            />
          </div>

          <div className="user-talent__content">{renderView()}</div>
        </>
      )}
    </PageWrapper>
  );
}
